import React, { useState, useMemo, useRef, useEffect, ElementType, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import useStore from 'state/knovStore'
import { MenuItemWithInfo } from './AiPopupMenuBase' // Assuming AiPopupMenuBase still exports this
import styles from './ai-magic-wand-menu-button.module.scss'
import cn from 'classnames'
import {
    Image as ImageIcon,
    Video as VideoIcon,
    Music as MusicIcon,
    Hammer,
    Shuffle,
    ChevronLeft,
} from 'lucide-react'
import usePanelContext from 'refactor/hooks/usePanelContext'
import { IAiModel, AiModelUIDefault } from 'state/aiModels'
import WithTooltip from 'components/shared/WithTooltip' // For the trigger button tooltips

// Original AiImageButtonIconSvg from AiMagicWandMenuButton.tsx
export function AiImageButtonIconSvg({
    color = 'currentColor',
    style = {},
    className = '',
    width = 24,
    height = 24,
}) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={style}
            className={className}
        >
            <defs />
            <g transform="matrix(-1,0,0,1,24.00387,0)">
                <path d="M 21.64,3.64 20.36,2.36 a 1.21,1.21 0 0 0 -1.72,0 L 2.36,18.64 a 1.21,1.21 0 0 0 0,1.72 l 1.28,1.28 a 1.2,1.2 0 0 0 1.72,0 L 21.64,5.36 a 1.2,1.2 0 0 0 0,-1.72" />
                <path d="m 14,7 3,3" />
                <path d="m 5,6 v 4" />
                <path d="m 19,14 v 4" />
                <path d="M 10,2 V 4" />
                <path d="M 7,8 H 3" />
                <path d="M 21,16 H 17" />
                <path d="M 11,3 H 9" />
            </g>
        </svg>
    )
}

const ToolItemsRaw: Omit<AiModelUIDefault, 'usdTreechatPublicPrice' | 'usdTreechatPrivatePrice'>[] =
    [
        {
            humanName: 'Create Gif',
            description: 'Convert a video to a gif',
            actionTagName: 'gif',
        },
        {
            humanName: 'Resize Image',
            description: 'Resize an image to a different size',
            actionTagName: 'resize',
        },
        {
            humanName: 'Pay Treechatters',
            description: 'Send a payment to another Treechat user',
            actionTagName: 'pay',
        },
        {
            humanName: 'Treesearch',
            description: 'Write a short report based on a web search',
            actionTagName: 'treesearch',
        },
    ]

// Types and Interfaces (from UnifiedAiActionsMenu)
type MenuItemType = (IAiModel | AiModelUIDefault) & {
    usdTreechatPublicPrice?: number
    usdTreechatPrivatePrice?: number
}

interface AiMagicWandMenuButtonProps {
    getEditor: () => any
    questIsPublic: boolean
    contextStyles?: string // For the main button container
    imageStyles?: string // For the icon itself
    // Remove props from old AiMagicWandMenuButton not used by unified menu:
    // models, setModel, activeModel, answer, quest (questIsPublic is used instead of full quest object)
}

type Category = 'image' | 'video' | 'audio' | 'tools' | 'remix'

interface Level1Item {
    id: Category
    label: string
    icon: ElementType
    menuTitle: string
    menuIcon: React.ReactElement
}

// Main component logic from UnifiedAiActionsMenu, adapted to be AiMagicWandMenuButton
export default function AiMagicWandMenuButton({
    getEditor,
    questIsPublic,
    contextStyles,
    imageStyles, // This might be for the SVG icon color or class
}: AiMagicWandMenuButtonProps) {
    const { panelId } = usePanelContext()
    const aiModels = useStore(state => state.aiModels)
    const [showMenu, setShowMenu] = useState(false)
    const [currentLevel, setCurrentLevel] = useState<1 | 2>(1)
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
    const [hover, setHover] = useState(false) // For main button hover state

    const buttonRef = useRef<HTMLDivElement>(null) // For the main trigger button
    const menuRef = useRef<HTMLDivElement>(null) // For the popup menu itself
    const [portalTargetNode, setPortalTargetNode] = useState<Element | null>(null)

    const SAFETY_PADDING = 10 // Padding from panel edges
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({
        opacity: 0,
        position: 'absolute',
        top: '-9999px',
    }) // Initial off-screen

    // Set a single, larger minHeight and guess, suitable for submenus.
    // Level 1 menu will have whitespace if its content is shorter.
    const DEFAULT_MENU_POPUP_HEIGHT_GUESS = 250
    const MIN_MENU_HEIGHT = 250

    // Height guess for initial placement decision (above/below button)
    const PLACEMENT_HEIGHT_GUESS = 220 // Based on L1 height
    // Min heights for L1 and L2
    const MIN_MENU_HEIGHT_L1 = 160
    const MIN_MENU_HEIGHT_L2 = 220 // L2 can be a bit taller by default

    const level1Items: Level1Item[] = [
        {
            id: 'image',
            label: 'Image',
            icon: ImageIcon,
            menuTitle: 'Image Generation',
            menuIcon: <ImageIcon size={20} />,
        },
        {
            id: 'video',
            label: 'Video',
            icon: VideoIcon,
            menuTitle: 'Video Generation',
            menuIcon: <VideoIcon size={20} />,
        },
        {
            id: 'audio',
            label: 'Audio',
            icon: MusicIcon,
            menuTitle: 'Audio Generation',
            menuIcon: <MusicIcon size={20} />,
        },
        {
            id: 'tools',
            label: 'Tools',
            icon: Hammer,
            menuTitle: 'Tools',
            menuIcon: <Hammer size={20} />,
        },
        {
            id: 'remix',
            label: 'Remix',
            icon: Shuffle,
            menuTitle: 'Remix Content',
            menuIcon: <Shuffle size={20} />,
        },
    ]

    const imageModels = useMemo(() => {
        const PRIORITY_NAMES = ['flux-1.1-pro', 'dall-e-3', 'gpt-image-1-create']

        const imageOnlyModels = aiModels.filter(
            m => m.modelType === 'image' && !m.canRemixImage && !m.canRemixVideo,
        )

        const prioritized = PRIORITY_NAMES.map(name =>
            imageOnlyModels.find(m => m.name === name),
        ).filter(Boolean)

        const remaining = imageOnlyModels
            .filter(m => !PRIORITY_NAMES.includes(m.name))
            .sort((a, b) => {
                const pub = a.usdTreechatPublicPrice - b.usdTreechatPublicPrice
                if (pub !== 0) return pub
                const priv = a.usdTreechatPrivatePrice - b.usdTreechatPrivatePrice
                if (priv !== 0) return priv
                return a.humanName.localeCompare(b.humanName)
            })

        return [...prioritized, ...remaining]
    }, [aiModels])

    const videoModels = useMemo(
        () =>
            aiModels
                .filter(m => m.modelType === 'video' && !m.canRemixVideo && !m.canRemixImage)
                .sort((a, b) => {
                    const priceComparison = a.usdTreechatPublicPrice - b.usdTreechatPublicPrice
                    if (priceComparison !== 0) return priceComparison
                    const privatePriceComparison =
                        a.usdTreechatPrivatePrice - b.usdTreechatPrivatePrice
                    if (privatePriceComparison !== 0) return privatePriceComparison
                    return a.humanName.localeCompare(b.humanName)
                }),
        [aiModels],
    )

    const audioModels = useMemo(
        () => aiModels.filter(m => m.modelType === 'audio' && !m.canRemixVideo && !m.canRemixImage),
        [aiModels],
    )

    const toolItems = useMemo(
        () =>
            ToolItemsRaw.map(tool => ({
                ...tool,
                usdTreechatPublicPrice: 0,
                usdTreechatPrivatePrice: 0,
            })),
        [],
    )

    const remixModels = useMemo(() => {
        const models = aiModels.filter(m => m.canRemixImage || m.canRemixVideo)
        // sort image models first, then video models, then by public price, private price, and finally by name
        return models.sort((a, b) => {
            if (a.modelType === 'image' && b.modelType !== 'image') return -1
            if (a.modelType !== 'image' && b.modelType === 'image') return 1
            const priceComparison = a.usdTreechatPublicPrice - b.usdTreechatPublicPrice
            if (priceComparison !== 0) return priceComparison
            const privatePriceComparison = a.usdTreechatPrivatePrice - b.usdTreechatPrivatePrice
            if (privatePriceComparison !== 0) return privatePriceComparison
            return a.humanName.localeCompare(b.humanName)
        })
    }, [aiModels])

    const currentMenuInfo = useMemo(() => {
        if (!selectedCategory) return null
        return level1Items.find(item => item.id === selectedCategory)
    }, [selectedCategory, level1Items])

    const toggleMenu = (e?: React.MouseEvent) => {
        e?.stopPropagation()
        if (showMenu) {
            setCurrentLevel(1)
            setSelectedCategory(null)
        }
        setShowMenu(prev => !prev)
    }

    const handleLevel1Select = (category: Category) => {
        setSelectedCategory(category)
        setCurrentLevel(2)
    }

    const handleBack = () => {
        setCurrentLevel(1)
        setSelectedCategory(null)
    }

    // Effect to determine and set the portal target DOM node
    useEffect(() => {
        if (showMenu) {
            let targetElement: Element | null = null
            if (panelId) {
                targetElement = document.querySelector(`[data-panel-id="${panelId}"]`)
            }
            setPortalTargetNode(targetElement || document.body) // Set to panel or fallback to body
        } else {
            setPortalTargetNode(null) // Clear portal target when menu is hidden
        }
    }, [showMenu, panelId])

    // Menu Positioning Logic - Handles both fixed (body) and absolute (panel) cases
    useEffect(() => {
        if (showMenu && buttonRef.current && menuRef.current && portalTargetNode) {
            const buttonRect = buttonRef.current.getBoundingClientRect()

            const currentMinMenuHeight =
                currentLevel === 1 ? MIN_MENU_HEIGHT_L1 : MIN_MENU_HEIGHT_L2

            if (buttonRect.width === 0 || buttonRect.height === 0) {
                setMenuStyle(prev => ({ ...prev, opacity: 0, top: '-9999px', zIndex: 99999 }))
                return
            }

            const menuWidth = 280
            const spaceBetweenButtonAndMenu = 5

            let newStyle: React.CSSProperties = {
                opacity: 1,
                width: `${menuWidth}px`,
                minHeight: `${currentMinMenuHeight}px`, // Use current level's minHeight
                zIndex: 99999,
                overflow: 'auto',
            }

            if (portalTargetNode === document.body) {
                // FIXED POSITIONING (Standalone mode, portaled to body)
                newStyle.position = 'fixed'
                let newTop, newLeft

                newLeft = buttonRect.left + buttonRect.width / 2 - menuWidth / 2
                if (newLeft < SAFETY_PADDING) newLeft = SAFETY_PADDING
                if (newLeft + menuWidth > window.innerWidth - SAFETY_PADDING) {
                    newLeft = window.innerWidth - menuWidth - SAFETY_PADDING
                }
                newStyle.left = `${newLeft}px`

                // Use PLACEMENT_HEIGHT_GUESS only for this initial top decision
                newTop = buttonRect.top - PLACEMENT_HEIGHT_GUESS - spaceBetweenButtonAndMenu
                if (newTop < SAFETY_PADDING) {
                    // If too high, try below
                    newTop = buttonRect.bottom + spaceBetweenButtonAndMenu
                }
                // Ensure newTop itself is within bounds after above/below decision
                if (newTop < SAFETY_PADDING) newTop = SAFETY_PADDING
                // Adjust newTop upwards if currentMinMenuHeight would overflow viewport bottom
                if (newTop + currentMinMenuHeight > window.innerHeight - SAFETY_PADDING) {
                    newTop = window.innerHeight - currentMinMenuHeight - SAFETY_PADDING
                    if (newTop < SAFETY_PADDING) newTop = SAFETY_PADDING // Re-check top after adjustment
                }
                newStyle.top = `${newTop}px`

                let dynamicMaxHeight = window.innerHeight - newTop - SAFETY_PADDING
                dynamicMaxHeight = Math.max(dynamicMaxHeight, currentMinMenuHeight) // MaxHeight must be at least minHeight
                newStyle.maxHeight = `${dynamicMaxHeight}px`
            } else {
                // ABSOLUTE POSITIONING (Inside a panel)
                newStyle.position = 'absolute'
                const panelRect = portalTargetNode.getBoundingClientRect()
                if (panelRect.width === 0 || panelRect.height === 0) {
                    setMenuStyle(prev => ({ ...prev, opacity: 0, top: '-9999px', zIndex: 99999 }))
                    return
                }

                let newLeftAbsolute, newMaxHeightAbsolute

                newLeftAbsolute = (panelRect.width - menuWidth) / 2
                newStyle.left = `${newLeftAbsolute}px`

                const spaceAboveInPanel =
                    buttonRect.top - panelRect.top - spaceBetweenButtonAndMenu - SAFETY_PADDING
                const spaceBelowInPanel =
                    panelRect.bottom -
                    buttonRect.bottom -
                    spaceBetweenButtonAndMenu -
                    SAFETY_PADDING

                // Use PLACEMENT_HEIGHT_GUESS for deciding above/below in panel
                if (
                    spaceAboveInPanel >= PLACEMENT_HEIGHT_GUESS ||
                    (spaceAboveInPanel > spaceBelowInPanel &&
                        spaceBelowInPanel < PLACEMENT_HEIGHT_GUESS)
                ) {
                    // Position with bottom (menu appears above button)
                    const newBottomAbsolute =
                        panelRect.height -
                        (buttonRect.top - panelRect.top) +
                        spaceBetweenButtonAndMenu
                    newStyle.bottom = `${newBottomAbsolute}px`
                    newStyle.top = 'auto'
                    newMaxHeightAbsolute = spaceAboveInPanel
                    // Adjust newBottomAbsolute if currentMinMenuHeight would overflow panel top
                    if (
                        panelRect.height - newBottomAbsolute + currentMinMenuHeight >
                        panelRect.height - SAFETY_PADDING
                    ) {
                        // This case is tricky, might need to adjust newBottom or accept minHeight forces it down
                    }
                } else {
                    // Position with top (menu appears below button)
                    const newTopAbsolute =
                        buttonRect.bottom - panelRect.top + spaceBetweenButtonAndMenu
                    newStyle.top = `${newTopAbsolute}px`
                    newStyle.bottom = 'auto'
                    newMaxHeightAbsolute = spaceBelowInPanel
                    // Adjust newTopAbsolute if currentMinMenuHeight would overflow panel bottom
                    if (newTopAbsolute + currentMinMenuHeight > panelRect.height - SAFETY_PADDING) {
                        // This might mean we need to reduce maxHeight or shift top further, but keep newTop stable unless absolutely necessary
                    }
                }

                newMaxHeightAbsolute = Math.max(newMaxHeightAbsolute, currentMinMenuHeight)
                newStyle.maxHeight = `${newMaxHeightAbsolute}px`
            }

            setMenuStyle(newStyle)
        } else {
            // When hiding, use a consistent position type (e.g. fixed or absolute from initial state) to avoid jumps if re-shown quickly
            // But ensure it's off-screen.
            setMenuStyle(prev => ({ ...prev, opacity: 0, top: '-9999px' }))
        }
    }, [
        showMenu,
        currentLevel,
        portalTargetNode,
        buttonRef,
        menuRef,
        SAFETY_PADDING,
        PLACEMENT_HEIGHT_GUESS,
        MIN_MENU_HEIGHT_L1,
        MIN_MENU_HEIGHT_L2,
    ])

    // Click outside handler (from UnifiedAiActionsMenu)
    useEffect(() => {
        if (!showMenu) return
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setShowMenu(false)
                setCurrentLevel(1)
                setSelectedCategory(null)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
        }
    }, [showMenu])

    const updatedToolItems = useMemo(
        () =>
            ToolItemsRaw.map(tool => ({
                ...tool,
                humanName: tool.humanName,
                description: tool.description,
                usdTreechatPublicPrice: 0,
                usdTreechatPrivatePrice: 0,
            })),
        [],
    )

    const updatedCurrentMenuItems = useMemo((): MenuItemType[] => {
        if (currentLevel === 1 || !selectedCategory) return []
        switch (selectedCategory) {
            case 'image':
                return imageModels
            case 'video':
                return videoModels
            case 'audio':
                return audioModels
            case 'tools':
                return updatedToolItems as MenuItemType[]
            case 'remix':
                return remixModels
            default:
                return []
        }
    }, [
        currentLevel,
        selectedCategory,
        imageModels,
        videoModels,
        audioModels,
        updatedToolItems,
        remixModels,
    ])

    // renderProperMenuContent (Updated for Level 2 header and back button integration)
    const renderProperMenuContent = () => {
        if (!showMenu || !portalTargetNode) return null

        let contentToRender

        if (currentLevel === 1) {
            contentToRender = (
                <div className={styles.levelOneItemListContainer}>
                    {level1Items.map(item => (
                        <div
                            key={item.id}
                            className={cn(
                                styles.aiMagicWandMenuButtonMenuItem,
                                styles.levelOneMenuItem,
                            )}
                            onClick={() => handleLevel1Select(item.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') handleLevel1Select(item.id)
                            }}
                        >
                            <item.icon size={18} className={styles.levelOneItemIcon} />
                            <span className={styles.menuItemText}>{item.label}</span>
                            <ChevronLeft
                                size={18}
                                className={styles.levelOneItemChevron}
                                style={{ transform: 'rotate(180deg)', marginLeft: 'auto' }}
                            />
                        </div>
                    ))}
                </div>
            )
        } else if (selectedCategory && currentMenuInfo) {
            contentToRender = (
                <div className={styles.aiMagicWandMenuButtonMenuLevel2}>
                    <div className={cn(styles.aiMagicWandMenuHeader, styles.submenuHeaderWithBack)}>
                        <div
                            className={styles.backButtonArea}
                            onClick={handleBack}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') handleBack()
                            }}
                        >
                            <ChevronLeft size={22} className={styles.backButtonIcon} />
                        </div>
                        <div className={styles.submenuHeaderTitleArea}>
                            {React.cloneElement(currentMenuInfo.menuIcon, {
                                className: styles.submenuHeaderIcon,
                            })}
                            <span className={styles.submenuHeaderText}>
                                {currentMenuInfo.menuTitle}
                            </span>
                        </div>
                    </div>
                    <div className={styles.submenuItemList}>
                        {updatedCurrentMenuItems.length > 0 ? (
                            updatedCurrentMenuItems.map((item, index) => (
                                <MenuItemWithInfo
                                    key={item.actionTagName || index}
                                    item={item}
                                    isActive={false}
                                    getEditor={getEditor}
                                    setShowMenu={() => {
                                        setShowMenu(false)
                                        setCurrentLevel(1)
                                        setSelectedCategory(null)
                                    }}
                                    isPublic={questIsPublic}
                                    showModelTypeIcon={selectedCategory !== 'tools'}
                                />
                            ))
                        ) : (
                            <div
                                className={styles.aiMagicWandMenuButtonMenuItem}
                                style={{ justifyContent: 'center', color: '#777' }}
                            >
                                No items available for this category.
                            </div>
                        )}
                    </div>
                </div>
            )
        }

        // Fallback debug content if contentToRender is still undefined/null
        if (!contentToRender) {
            contentToRender = (
                <div
                    style={{
                        padding: '20px',
                        color: 'purple',
                        backgroundColor: 'rgba(255,0,255,0.1)',
                    }}
                >
                    Debug: Menu content not generated. <br />
                    Current Level: {currentLevel} <br />
                    Selected Category: {selectedCategory || 'None'}
                </div>
            )
        }

        return createPortal(
            <div
                ref={menuRef}
                className={cn(styles.aiMagicWandMenuButtonMenu, styles.unifiedMenuContainer)}
                style={menuStyle} // menuStyle now includes opacity: 0 initially, then opacity: 1
                onClick={e => e.stopPropagation()}
            >
                {contentToRender}
            </div>,
            portalTargetNode, // Use the state variable for the portal target
        )
    }

    // Original trigger button structure from AiMagicWandMenuButton.tsx
    // The main container div uses `styles.aiMagicWandMenuButtonContainer` and the passed `contextStyles`.
    // The clickable part for opening the menu can be the whole area or specific parts.
    return (
        <div className={cn(styles.aiMagicWandMenuButtonContainer, contextStyles)} ref={buttonRef}>
            <WithTooltip tip="AI Actions">
                <div
                    className={styles.aiMagicWandMenuButtonUIMainComp}
                    onClick={toggleMenu}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') toggleMenu(e as any)
                    }}
                >
                    <div className={styles.toggleAiMagicWandMenuButton}>
                        <AiImageButtonIconSvg
                            className={cn(styles.aiMagicWandMenuButtonIcon, imageStyles)}
                            color="currentColor"
                        />
                    </div>
                    <div className={styles.aiMagicWandMenuButtonModel}>
                        <i className="fa fa-ellipsis-v" />
                    </div>
                </div>
            </WithTooltip>
            {renderProperMenuContent()}
        </div>
    )
}
